import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Form, Field } from 'react-final-form';
import { Button, ButtonTypes } from '../../../common/Button';
import { Loader } from '../../App/components/Loader';
import CopyableInput from './CopyableInput';
import { createClassroom } from '../../../firebase/classrooms';
import { showToast } from '../actions/toast';
import { setCurrentClassroom } from '../../User/actions';

/**
 * Create Classroom (staff only). One name field → createClassroom callable; on success we swap the
 * form for the freshly-minted join code so the instructor can share it. The code expires in 24h.
 */
const CreateClassroomModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [created, setCreated] = useState(null); // { joincode, expiresAt }
  const [busy, setBusy] = useState(false);

  function validate({ name }) {
    const errors = {};
    if (!name || name.trim().length === 0) {
      errors.name = t('CreateClassroomModal.EnterName', {
        defaultValue: 'Enter a classroom name.'
      });
    }
    return errors;
  }

  async function onSubmit({ name }) {
    setBusy(true);
    let submitError;
    try {
      const trimmedName = name.trim();
      const result = await createClassroom(trimmedName);
      setCreated(result);
      // Creating a classroom makes it the creator's current one (spec: auto-set on create).
      dispatch(setCurrentClassroom(result.classroomId, trimmedName));
      dispatch(showToast('Toast.ClassroomCreated', 3500));
    } catch (e) {
      submitError = {
        name: t('CreateClassroomModal.Error', {
          defaultValue: 'Could not create the classroom. Please try again.'
        })
      };
    } finally {
      setBusy(false);
    }
    return submitError;
  }

  if (busy) {
    return (
      <div className="classroom-modal" style={{ minHeight: '6rem' }}>
        <Loader />
      </div>
    );
  }

  if (created) {
    const expires = new Date(created.expiresAt).toLocaleString();
    return (
      <div className="classroom-modal">
        <p>
          {t('CreateClassroomModal.ShareCode', {
            defaultValue:
              'Share this join code with your students. It expires in 24 hours.'
          })}
        </p>
        <CopyableInput
          label={t('CreateClassroomModal.JoinCode', {
            defaultValue: 'Join code'
          })}
          value={created.joincode}
        />
        <p className="classroom-modal__expiry">
          {t('CreateClassroomModal.Expires', {
            defaultValue: 'Expires: {{when}}',
            when: expires
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="classroom-modal">
      <Form validate={validate} onSubmit={onSubmit}>
        {({ handleSubmit, submitting, touched, errors }) => (
          <form className="classroom-form" onSubmit={handleSubmit}>
            <Field name="name">
              {(field) => (
                <React.Fragment>
                  <label htmlFor="classroom-name">
                    {t('CreateClassroomModal.NameLabel', {
                      defaultValue: 'Classroom name'
                    })}
                  </label>
                  <input
                    id="classroom-name"
                    type="text"
                    maxLength="128"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    placeholder={t('CreateClassroomModal.NamePlaceholder', {
                      defaultValue: 'e.g. Period 3 - Fall'
                    })}
                    {...field.input}
                  />
                </React.Fragment>
              )}
            </Field>
            <Button type={ButtonTypes.SUBMIT} disabled={submitting}>
              {t('CreateClassroomModal.Submit', {
                defaultValue: 'Create classroom'
              })}
            </Button>
            {touched.name && errors.name && (
              <span className="form-error" aria-live="polite">
                {errors.name}
              </span>
            )}
          </form>
        )}
      </Form>
    </div>
  );
};

export default CreateClassroomModal;
